import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useTeam } from './TeamContext';

/**
 * Team management page — create teams, join by invite code, view memberships.
 */
export default function TeamManager() {
  const { isLocalMode, user } = useAuth();
  const { teams, currentTeam, loading: teamsLoading, createTeam, joinTeam, leaveTeam, switchTeam } = useTeam();

  const [newTeamName, setNewTeamName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (isLocalMode) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <h2 className="text-xl font-bold text-white mb-4">Teams</h2>
        <p className="text-gray-400">Enable Supabase to use team features.</p>
      </div>
    );
  }

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;

    setCreating(true);
    setError('');
    setSuccess('');

    const { error: err } = await createTeam(newTeamName.trim());
    if (err) {
      setError(err.message || 'Failed to create team');
    } else {
      setSuccess(`Team "${newTeamName.trim()}" created!`);
      setNewTeamName('');
    }
    setCreating(false);
  };

  const handleJoinTeam = async (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;

    setJoining(true);
    setError('');
    setSuccess('');

    const { data, error: err } = await joinTeam(inviteCode.trim());
    if (err) {
      setError(err.message || 'Invalid invite code');
    } else {
      setSuccess(`Joined team "${data.team_name}"!`);
      setInviteCode('');
    }
    setJoining(false);
  };

  const handleLeaveTeam = async (teamId, teamName) => {
    if (!window.confirm(`Leave "${teamName}"? You'll need an invite code to rejoin.`)) return;

    setError('');
    const { error: err } = await leaveTeam(teamId);
    if (err) {
      setError(err.message || 'Failed to leave team');
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    setSuccess('Invite code copied!');
    setTimeout(() => setSuccess(''), 2000);
  };

  return (
    <div className="px-4 py-6 max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-white mb-1">Teams</h1>
        <p className="text-gray-400 text-sm">Create, join, and manage your study teams</p>
      </div>

      {/* Loading state */}
      {teamsLoading && (
        <div className="text-center text-gray-500 py-8">Loading teams...</div>
      )}

      {/* Status messages */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-700/50 text-red-300 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg bg-green-900/30 border border-green-700/50 text-green-300 text-sm">
          {success}
        </div>
      )}

      {/* Create / Join forms */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        {/* Create team */}
        <form onSubmit={handleCreateTeam} className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Create a Team</h3>
          <input
            type="text"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            placeholder="Team name"
            maxLength={50}
            className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 mb-3"
          />
          <button
            type="submit"
            disabled={creating || !newTeamName.trim()}
            className="w-full py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
          >
            {creating ? 'Creating...' : 'Create Team'}
          </button>
        </form>

        {/* Join team */}
        <form onSubmit={handleJoinTeam} className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-white mb-3">Join a Team</h3>
          <input
            type="text"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            placeholder="Invite code"
            className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-blue-500 mb-3 font-mono"
          />
          <button
            type="submit"
            disabled={joining || !inviteCode.trim()}
            className="w-full py-2 rounded-lg bg-green-600 hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
          >
            {joining ? 'Joining...' : 'Join Team'}
          </button>
        </form>
      </div>

      {/* Team list */}
      <h2 className="text-lg font-semibold text-white mb-4">
        Your Teams {teams.length > 0 && <span className="text-gray-500 text-sm font-normal">({teams.length})</span>}
      </h2>

      {teams.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>You're not on any teams yet.</p>
          <p className="text-sm mt-1">Create one or join with an invite code above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {teams.map(team => {
            const isCurrent = currentTeam?.id === team.id;
            const isAdmin = team.role === 'admin';
            const isCreator = team.created_by === user?.id;

            return (
              <div
                key={team.id}
                className={`bg-gray-800/50 border rounded-xl p-4 transition-colors ${
                  isCurrent ? 'border-blue-500/50' : 'border-gray-700/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{team.name}</span>
                        {isCurrent && (
                          <span className="text-xs bg-blue-900/50 text-blue-300 px-2 py-0.5 rounded-full">
                            active
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          isAdmin ? 'bg-purple-900/50 text-purple-300' : 'bg-gray-700 text-gray-300'
                        }`}>
                          {team.role}
                        </span>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">Invite code:</span>
                          <code className="text-xs text-gray-400 font-mono bg-gray-700/50 px-1.5 py-0.5 rounded">
                            {team.invite_code}
                          </code>
                          <button
                            onClick={() => copyInviteCode(team.invite_code)}
                            className="text-xs text-blue-400 hover:text-blue-300"
                          >
                            copy
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {!isCurrent && (
                      <button
                        onClick={() => switchTeam(team.id)}
                        className="text-xs px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
                      >
                        Switch
                      </button>
                    )}
                    <button
                      onClick={() => handleLeaveTeam(team.id, team.name)}
                      className="text-xs px-3 py-1.5 rounded-lg text-red-400 hover:bg-red-900/30 transition-colors"
                    >
                      Leave
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
